import React from "react";
import axios from "axios";
import { Button } from "../../../../components/ui/button";
import { Sparkles, Loader2, RefreshCw, Undo2, Columns3 } from "lucide-react";
import { AICompareDialog } from "./AICompareDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TONE_OPTIONS = [
  { value: "balanced", label: "⚖️ Bilanciato (default)" },
  { value: "technical", label: "🔬 Ultra-tecnico (collezionisti esperti)" },
  { value: "commercial", label: "🔥 Commerciale/Emozionale (idea regalo)" },
  { value: "concise", label: "✂️ Conciso (max 80 parole)" },
  { value: "storytelling", label: "📖 Narrativo (storia & contesto)" },
];

/**
 * AI Describer Button — analyzes the currently-selected (or already-uploaded) product
 * photo with GPT-4.1 vision and pre-fills the form fields:
 *   title, description (it+en), brand, category, condition,
 *   ebay category_id, item specifics weight, etc.
 *
 * Fills DOM inputs directly (by id) so the parent doesn't need to manage state.
 */
export const AIDescriberButton = ({ selectedFile, existingImageUrl }) => {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [success, setSuccess] = React.useState(null);
  const [hasGenerated, setHasGenerated] = React.useState(false);
  const [tone, setTone] = React.useState("balanced");
  // Stack of previous form snapshots (last 5). Pushed before each AI generation.
  const [history, setHistory] = React.useState([]);
  // AI-generated variants (last 5), used by the Compare dialog
  const [variants, setVariants] = React.useState([]);
  const [compareOpen, setCompareOpen] = React.useState(false);

  const canAnalyze = !!selectedFile || !!existingImageUrl;

  // Read-only setter used by both fillFormFields and snapshot restore
  const setReactInput = (id, val) => {
    const el = document.getElementById(id);
    if (!el) return;
    const tag = el.tagName.toLowerCase();
    const proto = tag === "textarea" ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
    const nativeSetter = Object.getOwnPropertyDescriptor(proto, "value")?.set;
    const safeVal = val === undefined || val === null ? "" : String(val);
    if (nativeSetter) {
      nativeSetter.call(el, safeVal);
    } else {
      el.value = safeVal;
    }
    el.dispatchEvent(new Event("input", { bubbles: true }));
  };

  // Capture current form field values so we can restore them later
  const captureSnapshot = () => {
    const fields = ["title", "description", "category", "weight", "ebay_category_id"];
    const snap = {};
    fields.forEach((f) => {
      const el = document.getElementById(f);
      if (el) snap[f] = el.value || "";
    });
    const cond = document.getElementById("ebay_condition");
    if (cond) snap.ebay_condition = cond.value || "";
    return snap;
  };

  const restoreSnapshot = (snap) => {
    if (!snap) return;
    ["title", "description", "category", "weight", "ebay_category_id"].forEach((f) => {
      // Force-write the value even if empty (so users can fully revert)
      const el = document.getElementById(f);
      if (!el) return;
      const tag = el.tagName.toLowerCase();
      const proto = tag === "textarea" ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
      const nativeSetter = Object.getOwnPropertyDescriptor(proto, "value")?.set;
      const v = snap[f] ?? "";
      if (nativeSetter) nativeSetter.call(el, v);
      else el.value = v;
      el.dispatchEvent(new Event("input", { bubbles: true }));
    });
    const cond = document.getElementById("ebay_condition");
    if (cond && snap.ebay_condition !== undefined) {
      cond.value = snap.ebay_condition;
      cond.dispatchEvent(new Event("change", { bubbles: true }));
    }
  };

  const handleRestore = () => {
    if (history.length === 0) return;
    const last = history[history.length - 1];
    restoreSnapshot(last);
    setHistory((h) => h.slice(0, -1));
    setError(null);
    setSuccess(`↩️ Ripristinata versione precedente (${history.length - 1} ancora disponibili)`);
  };

  const fillFormFields = (data) => {
    const fillInput = (id, val) => {
      if (val === undefined || val === null || !String(val).trim()) return;
      setReactInput(id, val);
    };
    fillInput("title", data.title);
    const fullDesc =
      (data.description_it || "") +
      (data.description_en ? `\n\n--- English version ---\n${data.description_en}` : "");
    fillInput("description", fullDesc.trim());
    fillInput("category", data.category);
    fillInput("weight", data.weight_estimate_g);
    // NOTE: ebay_category_id and ebay_condition are NOT filled by AI anymore.
    // Admin sets them manually in the eBay section to avoid invalid-category errors.
  };

  const handleClick = async (regenerate = false) => {
    setError(null);
    setSuccess(null);
    setLoading(true);
    // Snapshot CURRENT form state before AI overwrites it (cap at last 5)
    const snap = captureSnapshot();
    setHistory((h) => [...h, snap].slice(-5));
    try {
      const token = sessionStorage.getItem("access_token");
      const fd = new FormData();
      // Backwards compat: selectedFile may now be an array of files
      const file = Array.isArray(selectedFile) ? selectedFile[0] : selectedFile;
      if (file) {
        fd.append("photo", file);
      } else if (existingImageUrl) {
        // Use server-side fetch to avoid CORS issues with R2/CDN images
        fd.append("image_url", existingImageUrl);
      } else {
        // No image available: pop the snapshot we just pushed
        setHistory((h) => h.slice(0, -1));
        setError("Carica prima una foto del prodotto.");
        setLoading(false);
        return;
      }
      fd.append("tone", tone);
      // Auto-retry once on transient Cloudflare proxy errors (520/504/522) and
      // network timeouts. GPT-4.1 vision occasionally hits a slow edge node
      // in production and bouncing the call resolves it 9/10 times.
      const callAI = () =>
        axios.post(`${API}/admin/ai-describe-product`, fd, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          timeout: 60000,
        });
      let resp;
      try {
        resp = await callAI();
      } catch (firstErr) {
        const status = firstErr.response?.status;
        const code = firstErr.code;
        const transient =
          status === 520 || status === 504 || status === 522 || status === 503 ||
          code === "ECONNABORTED" || code === "ETIMEDOUT";
        if (!transient) throw firstErr;
        // brief wait then retry once
        await new Promise((r) => setTimeout(r, 1500));
        setError("⏳ Risposta lenta dalla rete, riprovo...");
        resp = await callAI();
        setError("");
      }
      if (resp.data?.ok && resp.data?.data) {
        fillFormFields(resp.data.data);
        const d = resp.data.data;
        const toneLabel = TONE_OPTIONS.find((t) => t.value === tone)?.label || tone;
        // Save this variant for the Compare dialog (cap at last 5)
        setVariants((v) => [
          ...v,
          { tone, toneLabel, data: d, timestamp: Date.now() },
        ].slice(-5));
        setSuccess(
          `✅ ${regenerate ? "Rigenerato" : "Generato"} (${toneLabel}): titolo, descrizione (IT+EN), categoria "${d.category || "—"}", peso ${d.weight_estimate_g || "—"}g. ⚠️ Imposta manualmente Categoria/Condizione eBay nella sezione dedicata.`
        );
        setHasGenerated(true);
      } else {
        // AI didn't actually overwrite: pop the snapshot
        setHistory((h) => h.slice(0, -1));
        setError("Risposta AI inattesa");
      }
    } catch (err) {
      // AI call failed: nothing was overwritten, pop the snapshot
      setHistory((h) => h.slice(0, -1));
      setError(err.response?.data?.detail || err.message || "Errore analisi AI");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-3 flex flex-col gap-2">
      <Button
        type="button"
        onClick={() => handleClick(false)}
        disabled={loading || !canAnalyze}
        className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
        data-testid="ai-describer-btn"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Analisi AI in corso (5-15s)...
          </>
        ) : (
          <>
            <Sparkles className="w-4 h-4 mr-2" />
            🪄 Analizza foto con AI (GPT-4.1) e compila campi
          </>
        )}
      </Button>

      {/* Tone selector + Regenerate button: shown after first successful generation */}
      {hasGenerated && canAnalyze && (
        <div className="flex gap-2 items-center bg-purple-50 border border-purple-200 rounded p-2">
          <select
            value={tone}
            onChange={(e) => setTone(e.target.value)}
            disabled={loading}
            className="text-xs flex-1 bg-white border border-purple-300 rounded px-2 py-1.5 text-gray-700"
            data-testid="ai-tone-selector"
            aria-label="Seleziona tono di scrittura per rigenerazione AI"
          >
            {TONE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <Button
            type="button"
            onClick={() => handleClick(true)}
            disabled={loading}
            size="sm"
            variant="outline"
            className="border-purple-400 text-purple-700 hover:bg-purple-100 whitespace-nowrap"
            data-testid="ai-regenerate-btn"
          >
            {loading ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            )}
            Rigenera con AI
          </Button>
          <Button
            type="button"
            onClick={handleRestore}
            disabled={loading || history.length === 0}
            size="sm"
            variant="outline"
            className="border-amber-400 text-amber-700 hover:bg-amber-100 whitespace-nowrap disabled:opacity-40"
            data-testid="ai-restore-btn"
            title={history.length === 0 ? "Nessuna versione precedente da ripristinare" : `Ripristina la versione precedente (${history.length} disponibili)`}
          >
            <Undo2 className="w-3.5 h-3.5 mr-1.5" />
            Ripristina {history.length > 0 ? `(${history.length})` : ""}
          </Button>
          <Button
            type="button"
            onClick={() => setCompareOpen(true)}
            disabled={loading || variants.length < 2}
            size="sm"
            variant="outline"
            className="border-emerald-400 text-emerald-700 hover:bg-emerald-100 whitespace-nowrap disabled:opacity-40"
            data-testid="ai-compare-btn"
            title={variants.length < 2 ? "Servono almeno 2 generazioni AI per confrontare" : `Confronta ${variants.length} varianti AI affiancate`}
          >
            <Columns3 className="w-3.5 h-3.5 mr-1.5" />
            Confronta {variants.length >= 2 ? `(${variants.length})` : ""}
          </Button>
        </div>
      )}

      <AICompareDialog
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        variants={variants}
        onApply={(v) => {
          // Snapshot CURRENT before overwriting (so user can still revert)
          const snap = captureSnapshot();
          setHistory((h) => [...h, snap].slice(-5));
          fillFormFields(v.data);
          setCompareOpen(false);
          setSuccess(`✅ Applicata variante "${v.toneLabel}"`);
        }}
      />

      {!canAnalyze && (
        <p className="text-xs text-gray-500">Carica una foto sopra per abilitare l'analisi AI</p>
      )}
      {error && (
        <p className="text-xs text-red-600 bg-red-50 p-2 rounded">❌ {error}</p>
      )}
      {success && (
        <p className="text-xs text-green-700 bg-green-50 p-2 rounded">{success}</p>
      )}
    </div>
  );
};
